import { FieldFunctionOptions } from "@apollo/client";
import { uniqBy } from "lodash";

interface ResponseObject {
  __typename?: string;
}

interface PaginatedResponse<T> {
  totalCount: number;
  objects: T[];
}

export const mergePaginatedData = <T extends ResponseObject>(
  existing: Pick<PaginatedResponse<T>, "objects"> | undefined,
  incoming: PaginatedResponse<T>,
  options: FieldFunctionOptions,
): PaginatedResponse<T> => {
  const existingObjects = existing?.objects || [];
  const incomingObjects = incoming.objects || [];
  const paginationArgs = options?.args?.pagination || {
    offset: 0,
    limit: incomingObjects.length,
  };
  const { offset, limit } = paginationArgs;
  const mergedObjects = [...existingObjects];
  mergedObjects.splice(offset, limit, ...incomingObjects);
  return {
    totalCount: incoming.totalCount,
    objects: uniqBy(mergedObjects, "__ref"),
  };
};

interface ActionPermissionsResponse<T> {
  totalCount?: number | null;
  enabledCount?: number | null;
  objects?: T[];
}

export const mergeActionPermissionsData = <T>(
  existing: ActionPermissionsResponse<T> | undefined,
  incoming: ActionPermissionsResponse<T>,
  options: FieldFunctionOptions,
): ActionPermissionsResponse<T> => {
  const merged = { ...existing, ...incoming };
  // EmployeeActionPermission rows have no id, so they are stored inline and
  // cannot be deduped by ref; splice strictly by the requested page window.
  if (incoming.objects === undefined) return merged;
  const incomingObjects = incoming.objects;
  const pagination = options?.args?.pagination;
  if (!pagination) return { ...merged, objects: incomingObjects };
  const { offset = 0, limit = incomingObjects.length } = pagination;
  const objects = [...(existing?.objects ?? [])];
  objects.splice(offset, limit, ...incomingObjects);
  return { ...merged, objects };
};
